import React, { useEffect, useRef, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonIcon, IonText, IonAlert, IonSpinner, IonCheckbox,
} from '@ionic/react';
import { closeOutline, trashOutline, cloudUploadOutline } from 'ionicons/icons';
import { apiService } from '@/services/ApiService';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';

interface ModuleImageModalProps {
    isOpen: boolean;
    onDidDismiss: () => void;
    currentLogo: string | null;
    canEditAttachments: boolean;
    onSaved: () => void;
}

const ModuleImageModal: React.FC<ModuleImageModalProps> = ({
    isOpen, onDidDismiss, currentLogo, canEditAttachments, onSaved,
}) => {
    const [images, setImages] = useState<string[]>([]);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [originalSelected, setOriginalSelected] = useState<string | null>(null);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [overwrite, setOverwrite] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const forceClosingRef = useRef(false);

    useEffect(() => {
        if (isOpen) {
            forceClosingRef.current = false;
            setSelectedImage(currentLogo ?? null);
            setOriginalSelected(currentLogo ?? null);
            setUploadFile(null);
            setOverwrite(false);
            loadImages();
        }
    }, [isOpen, currentLogo]);

    const loadImages = async () => {
        setIsLoading(true);
        try {
            const imgs = await apiService.getContractImages();
            setImages(imgs);
        } catch {
            setToast({ message: 'Failed to load images.', variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpload = async () => {
        if (!uploadFile) return;
        const duplicate = images.includes(uploadFile.name);
        if (duplicate && !overwrite) {
            setToast({ message: 'A file with that name already exists. Enable Overwrite to replace it.', variant: 'warning' });
            return;
        }
        setIsSaving(true);
        const formData = new FormData();
        formData.append('file', uploadFile);
        try {
            await apiService.uploadModuleImage(formData);
            setUploadFile(null);
            setOverwrite(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
            setToast({ message: 'Image uploaded.', variant: 'success' });
            await loadImages();
        } catch {
            setToast({ message: 'Failed to upload image.', variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setShowDeleteAlert(false);
        setIsSaving(true);
        try {
            await apiService.deleteModuleImage(deleteTarget);
            if (selectedImage === deleteTarget) setSelectedImage(null);
            setToast({ message: 'Image deleted.', variant: 'success' });
            await loadImages();
        } catch {
            setToast({ message: 'Failed to delete image.', variant: 'error' });
        } finally {
            setIsSaving(false);
            setDeleteTarget(null);
        }
    };

    const handleSaveLogo = async () => {
        if (selectedImage === originalSelected) return;
        setIsSaving(true);
        try {
            await apiService.saveModuleLogo(selectedImage ?? '');
            setOriginalSelected(selectedImage);
            setToast({ message: 'Module logo saved.', variant: 'success' });
            onSaved();
        } catch {
            setToast({ message: 'Failed to save module logo.', variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const hasUnsavedSelection = selectedImage !== originalSelected;

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={() => { if (!forceClosingRef.current) onDidDismiss(); }}
                style={{ '--width': '620px', '--height': '80vh', '--border-radius': '8px' }}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle style={{ fontSize: '1rem' }}>Module Image</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={onDidDismiss}><IonIcon icon={closeOutline} /></IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent style={{ '--padding-start': '1rem', '--padding-end': '1rem', '--padding-top': '1rem', '--padding-bottom': '1rem' }}>
                    {(isLoading || isSaving) ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                            <IonSpinner />
                        </div>
                    ) : (
                        <>
                            {/* Upload section */}
                            {canEditAttachments && (
                                <div style={{ background: 'var(--ion-color-light)', borderRadius: '6px', padding: '1rem', marginBottom: '1rem', border: '1px solid var(--ion-color-light-shade)' }}>
                                    <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Upload Image</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            style={{ flex: 1, minWidth: '200px' }}
                                            onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                                        />
                                        <IonCheckbox checked={overwrite} onIonChange={(e) => setOverwrite(e.detail.checked)}>Overwrite</IonCheckbox>
                                        <IonButton fill="solid" color="primary" size="small" disabled={!uploadFile} onClick={handleUpload}>
                                            <IonIcon icon={cloudUploadOutline} slot="start" /> Upload
                                        </IonButton>
                                    </div>
                                </div>
                            )}

                            {/* Image list */}
                            {images.length === 0 ? (
                                <IonText color="medium" style={{ display: 'block', textAlign: 'center', padding: '2rem' }}>
                                    No images available.
                                </IonText>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                                    {images.map(img => (
                                        <div key={img}
                                            onClick={() => setSelectedImage(img === selectedImage ? null : img)}
                                            style={{
                                                border: selectedImage === img ? '3px solid var(--ion-color-primary)' : '2px solid var(--ion-color-light-shade)',
                                                borderRadius: '6px', padding: '0.5rem', cursor: 'pointer',
                                                background: selectedImage === img ? 'var(--ion-color-primary-tint)' : 'var(--ion-color-light)',
                                                position: 'relative',
                                            }}>
                                            <div style={{ fontSize: '0.8rem', wordBreak: 'break-word', textAlign: 'center' }}>{img}</div>
                                            {canEditAttachments && (
                                                <IonButton fill="clear" size="small" color="danger"
                                                    style={{ position: 'absolute', top: 0, right: 0 }}
                                                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(img); setShowDeleteAlert(true); }}>
                                                    <IonIcon icon={trashOutline} />
                                                </IonButton>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {selectedImage && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '0.5rem', borderTop: '1px solid var(--ion-color-light-shade)' }}>
                                    <IonText style={{ marginRight: 'auto', alignSelf: 'center', fontSize: '0.9rem', color: 'var(--ion-color-medium)' }}>
                                        Selected: {selectedImage}
                                    </IonText>
                                    <IonButton fill="solid" color="primary" disabled={!hasUnsavedSelection} onClick={handleSaveLogo}>
                                        Set as Logo
                                    </IonButton>
                                </div>
                            )}
                        </>
                    )}
                </IonContent>
            </IonModal>

            <IonAlert isOpen={showDeleteAlert} header="Delete Image" message={`Delete "${deleteTarget}"?`}
                buttons={[
                    { text: 'Cancel', role: 'cancel', handler: () => { setShowDeleteAlert(false); setDeleteTarget(null); } },
                    { text: 'Delete', role: 'destructive', handler: handleDeleteConfirm },
                ]}
                onDidDismiss={() => setShowDeleteAlert(false)}
            />
            <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
        </>
    );
};

export default ModuleImageModal;
